import * as Sentry from '@sentry/vue';
import { type Query, useQuery } from '@tanstack/vue-query';
import { type ComputedRef, computed, shallowRef } from 'vue';
import { queryClient } from '@/app/query-client';
import { digitalExtrasQuery } from '@/services/api/endpoints/customer_download';
import { httpStatusOf } from '@/services/api/http';
import type { DigitalExtrasResponse } from '@/shared/types/api';
import type { DigitalExtra } from './extra-files';

const POLL_INTERVAL_MS = 4000;

export interface CustomerDigitalExtrasView {
  extras: DigitalExtra[];
  failed: boolean;
  fetching: boolean;
  loading: boolean;
  downloadCode?: string;
}

export interface CustomerDigitalExtrasState {
  hasExtras: ComputedRef<boolean>;
  view: ComputedRef<CustomerDigitalExtrasView>;
  load: (code: string) => Promise<void>;
  retry: () => Promise<void>;
  stop: () => void;
}

function videos(response: DigitalExtrasResponse): DigitalExtra[] {
  return (response.extras as DigitalExtra[]).filter(
    (extra) => extra.kind === 'video'
  );
}

// After a failure only the connection or a retry asks again.
function pollInterval(query: Query<DigitalExtrasResponse>): number | false {
  const { data, status } = query.state;
  return status === 'success' &&
    data &&
    videos(data).some(
      (extra) => extra.status === 'queued' || extra.status === 'running'
    )
    ? POLL_INTERVAL_MS
    : false;
}

function canReconnect(query: Query<DigitalExtrasResponse>): boolean {
  if (query.state.status !== 'error') {
    return false;
  }
  const status = httpStatusOf(query.state.error);
  return (
    status === undefined || status === 408 || status === 429 || status >= 500
  );
}

// One list per page load, shared by the video tab and the cashier order, so
// switching tabs neither refetches it nor restarts the preparation polling.
export function useCustomerDigitalExtras(): CustomerDigitalExtrasState {
  const code = shallowRef('');

  const query = useQuery(
    computed(() => {
      const extrasQuery = digitalExtrasQuery(code.value);
      return {
        queryKey: extrasQuery.queryKey,
        queryFn: async (
          context: Parameters<typeof extrasQuery.queryFn>[0]
        ): Promise<DigitalExtrasResponse> => {
          try {
            return await extrasQuery.queryFn(context);
          } catch (error) {
            if (!context.signal.aborted) {
              Sentry.captureException(error, {
                tags: { feature: 'digital_extras', stage: 'download_list' },
              });
            }
            throw error;
          }
        },
        enabled: code.value !== '',
        staleTime: 0,
        refetchInterval: pollInterval,
        refetchOnReconnect: canReconnect,
      };
    }),
    queryClient
  );

  const extras = computed(() =>
    query.data.value ? videos(query.data.value) : []
  );

  async function load(nextCode: string): Promise<void> {
    stop();
    code.value = nextCode;
    await query.suspense();
  }

  async function retry(): Promise<void> {
    if (code.value) {
      await query.refetch({ cancelRefetch: false });
    }
  }

  function stop(): void {
    const current = code.value;
    code.value = '';
    if (current) {
      const { queryKey } = digitalExtrasQuery(current);
      void queryClient.cancelQueries({ queryKey });
      queryClient.removeQueries({ queryKey });
    }
  }

  return {
    hasExtras: computed(() => extras.value.length > 0),
    view: computed(() => ({
      extras: extras.value,
      failed: query.isError.value,
      fetching: query.isFetching.value,
      loading: query.isFetching.value && extras.value.length === 0,
      downloadCode: code.value,
    })),
    load,
    retry,
    stop,
  };
}
